import { HttpError } from "@microbeta/common-frontend";
import { Button, Heading, Text } from "@microbeta/ui";
import { useState } from "react";
import type { FallbackProps } from "react-error-boundary";
import { ErrorBoundary as ReactErrorBoundary } from "react-error-boundary";
import { useTranslation } from "react-i18next";
import { MdBugReport, MdErrorOutline, MdHome, MdRefresh } from "react-icons/md";
import styles from "./ErrorBoundary.module.scss";

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

function ErrorFallback({
  error,
  resetErrorBoundary,
}: FallbackProps): React.ReactElement {
  const { t } = useTranslation();
  const [showDetails, setShowDetails] = useState(false);
  let message = t(
    "errorBoundary.defaultMessage",
    "Something went wrong. Please try again later."
  );
  let title = t("errorBoundary.defaultTitle", "Oops! An Error Occurred");

  if (error instanceof HttpError) {
    title = t("errorBoundary.httpErrorTitle", "Network Error");
    switch (error.status) {
      case 401:
        message = t(
          "errorBoundary.unauthorized",
          "You are not authorized to view this page."
        );
        break;
      case 403:
        message = t(
          "errorBoundary.forbidden",
          "You do not have permission to access this resource."
        );
        break;
      case 404:
        message = t(
          "errorBoundary.notFound",
          "The page you are looking for could not be found."
        );
        break;
      case 500:
        message = t(
          "errorBoundary.serverError",
          "There was a problem with our server. Please try again later."
        );
        break;
      default:
        message =
          error.response?.message ||
          t(
            "errorBoundary.genericHttpError",
            "An unknown network error occurred."
          );
    }
  } else if (error instanceof Error) {
    // For generic errors, you might want to log them but show a generic message
    console.error("ErrorBoundary caught an error:", error);
  }

  const handleGoHome = () => {
    window.location.href = "/";
  };

  return (
    <div className={styles.errorContainer} role="alert">
      <div className={styles.errorCard}>
        <div className={styles.errorIcon}>
          <MdErrorOutline />
        </div>
        <Heading as="h1" variant="large" className={styles.errorTitle}>
          {title}
        </Heading>
        <Text variant="medium" className={styles.errorMessage}>
          {message}
        </Text>

        <div className={styles.errorActions}>
          <Button onClick={resetErrorBoundary} variant="primary">
            <MdRefresh style={{ marginRight: "8px" }} />
            {t("errorBoundary.tryAgain", "Try Again")}
          </Button>
          <Button onClick={handleGoHome} variant="outline">
            <MdHome style={{ marginRight: "8px" }} />
            {t("errorBoundary.goHome", "Go Home")}
          </Button>
        </div>

        {import.meta.env.MODE === "development" && (
          <>
            <Button
              onClick={() => setShowDetails(!showDetails)}
              variant="ghost"
              style={{ marginTop: "var(--spacing-md)" }}
            >
              <MdBugReport style={{ marginRight: "8px" }} />
              {showDetails
                ? t("errorBoundary.hideDetails", "Hide Details")
                : t("errorBoundary.showDetails", "Show Details")}
            </Button>

            {showDetails && (
              <details className={styles.errorDetails} open>
                <summary className={styles.errorDetailsSummary}>
                  {t("errorBoundary.errorDetails", "Error Details")}
                </summary>
                <pre className={styles.errorStack}>
                  {error.name}: {error.message}
                  {error.stack && `\n\n${error.stack}`}
                </pre>
              </details>
            )}
          </>
        )}

        <Text
          variant="small"
          style={{
            marginTop: "var(--spacing-lg)",
            textAlign: "center",
            color: "var(--color-text-tertiary)",
          }}
        >
          {t(
            "errorBoundary.support",
            "If the problem persists, please contact support."
          )}
        </Text>
      </div>
    </div>
  );
}

export const ErrorBoundary: React.FC<ErrorBoundaryProps> = ({ children }) => {
  return (
    <ReactErrorBoundary FallbackComponent={ErrorFallback}>
      {children}
    </ReactErrorBoundary>
  );
};
